import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Clock, Filter } from "lucide-react";
import { Layout } from "@/components/Layout";
import { NewsCard, EventCard } from "@/components/Cards";
import { newsItems, events } from "@/lib/index";
import { fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { IMAGES } from "@/assets/images";

type NewsCategory = "الكل" | "أخبار الكلية" | "إنجازات الطلاب" | "فعاليات" | "إعلانات";

export default function News() {
  const [selectedCategory, setSelectedCategory] = useState<NewsCategory>("الكل");

  const categories: NewsCategory[] = [
    "الكل",
    "أخبار الكلية",
    "إنجازات الطلاب",
    "فعاليات",
    "إعلانات",
  ];

  const filteredNews =
    selectedCategory === "الكل"
      ? newsItems
      : newsItems.filter((item) => item.category === selectedCategory);

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <section
          className="relative py-24 overflow-hidden"
          style={{
            backgroundImage: `url(${IMAGES.GRADUATION_8})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-primary/95 via-primary/85 to-primary/95" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="text-center text-white"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                الأخبار والفعاليات
              </h1>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                تابع آخر أخبار الكلية وفعالياتها وإنجازات طلابنا المتميزين
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <Tabs defaultValue="news" className="w-full" dir="rtl">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
                <TabsTrigger value="news" className="text-lg">
                  الأخبار
                </TabsTrigger>
                <TabsTrigger value="events" className="text-lg">
                  الفعاليات
                </TabsTrigger>
              </TabsList>

              <TabsContent value="news">
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={staggerContainer}
                >
                  <div className="flex items-center gap-3 mb-8 flex-wrap">
                    <Filter className="w-5 h-5 text-muted-foreground" />
                    <span className="text-muted-foreground font-medium">
                      تصفية حسب:
                    </span>
                    {categories.map((category) => (
                      <Button
                        key={category}
                        variant={selectedCategory === category ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedCategory(category)}
                        className="rounded-full"
                      >
                        {category}
                      </Button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredNews.map((news) => (
                      <motion.div key={news.id} variants={staggerItem}>
                        <NewsCard news={news} />
                      </motion.div>
                    ))}
                  </div>

                  {filteredNews.length === 0 && (
                    <div className="text-center py-16">
                      <p className="text-muted-foreground text-lg">
                        لا توجد أخبار في هذه الفئة حالياً
                      </p>
                    </div>
                  )}
                </motion.div>
              </TabsContent>

              <TabsContent value="events">
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={staggerContainer}
                >
                  <div
                    className="relative mb-12 rounded-2xl overflow-hidden"
                    style={{
                      backgroundImage: `url(${IMAGES.STUDENTS_LAB_1})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-accent/80" />
                    <div className="relative z-10 py-12 px-8 text-white">
                      <h2 className="text-3xl font-bold mb-4">
                        الفعاليات القادمة
                      </h2>
                      <p className="text-white/90 text-lg">
                        انضم إلينا في فعالياتنا المتنوعة وورش العمل التدريبية
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {events.map((event) => (
                      <motion.div key={event.id} variants={staggerItem}>
                        <EventCard event={event} />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl font-bold mb-6">ابق على اطلاع دائم</h2>
              <p className="text-muted-foreground text-lg mb-8">
                اشترك في نشرتنا الإخبارية لتصلك آخر الأخبار والفعاليات مباشرة
              </p>
              <div className="flex gap-4 max-w-md mx-auto" dir="rtl">
                <input
                  type="email"
                  placeholder="أدخل بريدك الإلكتروني"
                  className="flex-1 px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button size="lg" className="px-8">
                  اشترك
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
